import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { buildProfitabilitySummary } from '../reportesFinancieros';

dayjs.extend(utc);
dayjs.extend(timezone);
const REPORT_TIMEZONE = 'America/Lima';

const normalizarPlataforma = value => {
  const platform = String(value || '').trim();
  return platform.toLowerCase() === 'gpt' ? 'GPT' : platform;
};

const moneyToCents = value => {
  const match = String(value ?? '').trim().match(/^(0|[1-9]\d*)(?:\.(\d{1,2}))?$/);
  return match ? Number(match[1]) * 100 + Number(String(match[2] || '').padEnd(2, '0')) : 0;
};
const sumMoney = (items, select) => items.reduce((cents, item) => cents + moneyToCents(select(item)), 0) / 100;

function resumirPeriodo(transacciones, inventario, desde, hasta) {
  const movimientos = transacciones.filter(tx => {
    if (!tx.date) return false;
    const fecha = dayjs(tx.date).tz(REPORT_TIMEZONE);
    return fecha.valueOf() >= desde.valueOf() && fecha.valueOf() < hasta.valueOf();
  });
  const ingresos = movimientos.filter(tx => ['VENTA', 'RENOVACION'].includes(String(tx.type || '').toUpperCase()));
  const ventas = ingresos.filter(tx => String(tx.type || '').toUpperCase() === 'VENTA').length;
  const renovaciones = ingresos.length - ventas;
  const ingresoTotal = sumMoney(ingresos, tx => tx.amount);
  const gastosStock = sumMoney(
    movimientos.filter(tx => String(tx.type || '').toUpperCase() === 'GASTO_STOCK'),
    tx => tx.amount
  );
  const rentabilidad = buildProfitabilitySummary(ingresos, inventario);

  return {
    ingresoTotal,
    operaciones: ingresos.length,
    ventas,
    renovaciones,
    ticketPromedio: ingresos.length ? ingresoTotal / ingresos.length : 0,
    gastosStock,
    gananciaPerfiles: rentabilidad.totalProfit,
    ventasConCostoFaltante: rentabilidad.missingCostCount
  };
}

function VistaReportes({ api }) {
  const [transacciones, setTransacciones] = useState([]);
  const [inventario, setInventario] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtroMes, setFiltroMes] = useState('TODOS');
  const [busqueda, setBusqueda] = useState('');

  // --- CARGAR DATOS FINANCIEROS ---
  const cargarReportes = useCallback(() => {
    setCargando(true);
    const ts = Date.now();

    Promise.all([
      axios.get(`${api}/reportes/transacciones?t=${ts}`).catch(err => {
        console.error("Error al cargar transacciones:", err);
        return { data: [] };
      }),
      axios.get(`${api}/inventario?t=${ts}`).catch(err => {
        console.error("Error al cargar inventario:", err);
        return { data: [] };
      })
    ]).then(([resTx, resInv]) => {
      const txData = Array.isArray(resTx.data)
  ? resTx.data
  : (resTx.data?.data || []);

const invData = Array.isArray(resInv.data)
  ? resInv.data
  : (resInv.data?.data || []);

// Normalizar nombre del cliente recibido desde el backend
const txNormalizadas = txData.map(tx => {
  const tipo = String(tx.type || '').toUpperCase();

  const nombreCliente =
    tx.client_name ||
    tx.nombre_cliente ||
    tx.customer_name ||
    tx.cliente ||
    (tipo === 'GASTO_STOCK' ? 'STOCK' : '—');

  return {
    ...tx,
    client_name: nombreCliente,
    platform: normalizarPlataforma(tx.platform)
  };
});
setTransacciones(txNormalizadas);
setInventario(invData);

    }).finally(() => {
      setCargando(false);
    });
  }, [api]);

  useEffect(() => {
    cargarReportes();
  }, [cargarReportes]);

  // --- LISTA DE MESES DISPONIBLES EN TRANSACCIONES ---
  const opcionesMeses = useMemo(() => {
    const mesesSet = new Set();
    transacciones.forEach(tx => {
      if (tx.date) {
        mesesSet.add(dayjs(tx.date).tz(REPORT_TIMEZONE).format('YYYY-MM'));
      }
    });
    return Array.from(mesesSet).sort().reverse();
  }, [transacciones]);

  // --- FILTRADO DE TRANSACCIONES POR MES Y BÚSQUEDA ---
  const transaccionesFiltradas = useMemo(() => {
    return transacciones.filter(tx => {
      // Filtro por Mes/Periodo
      if (filtroMes !== 'TODOS') {
        const fechaTx = dayjs(tx.date).tz(REPORT_TIMEZONE).format('YYYY-MM');
        if (fechaTx !== filtroMes) return false;
      }
      // Filtro por Búsqueda de texto
      if (busqueda.trim() !== '') {
        const term = busqueda.toLowerCase();
        const cliente = `${tx.client_name || ''} ${tx.customer_phone || ''} ${tx.customer_id || ''}`.toLowerCase();
        const plat = `${tx.platform || ''} ${tx.profile_label || ''} ${tx.account_id || ''} ${tx.subscription_id || ''}`.toLowerCase();
        const tipo = (tx.type || '').toLowerCase();
        const desc = (tx.description || '').toLowerCase();
        return cliente.includes(term) || plat.includes(term) || tipo.includes(term) || desc.includes(term);
      }
      return true;
    });
  }, [transacciones, filtroMes, busqueda]);

  // --- CÁLCULOS KPI PRINCIPALES ---
  const transaccionesIngresoFiltradas = useMemo(() => {
    return transaccionesFiltradas.filter(tx => ['VENTA', 'RENOVACION'].includes(String(tx.type || '').toUpperCase()));
  }, [transaccionesFiltradas]);

  const liquidezTotal = useMemo(() => {
    return sumMoney(transaccionesIngresoFiltradas, tx => tx.amount);
  }, [transaccionesIngresoFiltradas]);

  const inversionStock = useMemo(() => {
    return sumMoney(inventario, inv => inv.costo);
  }, [inventario]);

  const gastosStockPeriodo = useMemo(() => {
    return sumMoney(
      transaccionesFiltradas.filter(tx => String(tx.type || '').toUpperCase() === 'GASTO_STOCK'),
      tx => tx.amount
    );
  }, [transaccionesFiltradas]);

  const rentabilidad = useMemo(
    () => buildProfitabilitySummary(transaccionesFiltradas, inventario),
    [transaccionesFiltradas, inventario]
  );

  const gananciaNetaReal = useMemo(() => {
    return rentabilidad.totalProfit;
  }, [rentabilidad]);

  const totalVentasCount = transaccionesIngresoFiltradas.length;

  const periodosReporte = useMemo(() => {
    const ahora = dayjs().tz(REPORT_TIMEZONE);
    const inicioDia = ahora.startOf('day');
    const inicioSemana = inicioDia.subtract((inicioDia.day() + 6) % 7, 'day');
    return {
      diario: {
        desde: inicioDia,
        hasta: inicioDia.add(1, 'day'),
        label: inicioDia.format('DD/MM/YYYY')
      },
      semanal: {
        desde: inicioSemana,
        hasta: inicioSemana.add(7, 'day'),
        label: `${inicioSemana.format('DD/MM/YYYY')} – ${inicioSemana.add(6, 'day').format('DD/MM/YYYY')}`
      }
    };
  }, []);

  const reportesPeriodo = useMemo(() => ({
    diario: resumirPeriodo(transacciones, inventario, periodosReporte.diario.desde, periodosReporte.diario.hasta),
    semanal: resumirPeriodo(transacciones, inventario, periodosReporte.semanal.desde, periodosReporte.semanal.hasta)
  }), [transacciones, inventario, periodosReporte]);

  // --- DESGLOSE DE LIQUIDEZ POR PLATAFORMA ---
  const desglosePlataformas = useMemo(() => {
    return rentabilidad.platforms.map(item => ({
      ...item,
      porcentaje: liquidezTotal > 0 ? ((item.revenue / liquidezTotal) * 100).toFixed(1) : '0'
    }));
  }, [rentabilidad, liquidezTotal]);

  // --- COLORES BRAND PARA PLATAFORMAS ---
  const getBrandColor = (plat) => {
    const s = (plat || '').toLowerCase();
    if (s.includes('netflix')) return { bg: '#E50914', text: '#ffffff' };
    if (s.includes('disney')) return { bg: '#113CCF', text: '#ffffff' };
    if (s.includes('hbo max') || s.includes('hbo')) return { bg: '#991EEB', text: '#ffffff' };
    if (s.includes('prime video')) return { bg: '#00A8E1', text: '#ffffff' };
    if (s.includes('spotify')) return { bg: '#1DB954', text: '#ffffff' };
    if (s.includes('crunchyroll')) return { bg: '#F47521', text: '#ffffff' };
    if (s.includes('vix')) return { bg: '#FF5A00', text: '#ffffff' };
    if (s.includes('paramount')) return { bg: '#0064FF', text: '#ffffff' };
    if (s.includes('gpt')) return { bg: '#00A8E1', text: '#ffffff' };
    return { bg: '#4A5568', text: '#ffffff' };
  };

  return (
    <div className="container-fluid px-0">
      
      {/* 🚀 CABECERA Y FILTRO DE PERIODO */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3 bg-white p-3 rounded-3 shadow-sm border">
        <div>
          <h4 className="fw-bold mb-1 text-dark d-flex align-items-center gap-2">
            <i className="bi bi-pie-chart-fill text-success"></i>
            Reportes Financieros e Historial de Liquidez
          </h4>
          <p className="text-muted small mb-0">
            Ingresos, costo por perfil según la capacidad de cada cuenta y rentabilidad por plataforma.
          </p>
        </div>

        <div className="d-flex align-items-center gap-2">
          <label className="fw-bold small text-muted mb-0 me-1">
            <i className="bi bi-calendar-event me-1"></i>Periodo:
          </label>
          <select 
            className="form-select form-select-sm fw-bold border-success shadow-sm"
            style={{ width: '180px', borderRadius: '20px' }}
            value={filtroMes}
            onChange={(e) => setFiltroMes(e.target.value)}
          >
            <option value="TODOS">📅 Todos los Meses</option>
            {opcionesMeses.map(m => (
              <option key={m} value={m}>
                {dayjs(m).format('MMMM YYYY').toUpperCase()}
              </option>
            ))}
          </select>

          <button 
            onClick={cargarReportes}
            className="btn btn-sm btn-outline-secondary rounded-circle p-2 d-flex align-items-center justify-content-center"
            title="Recargar datos de Supabase"
            style={{ width: '34px', height: '34px' }}
          >
            <i className={`bi bi-arrow-clockwise ${cargando ? 'spin' : ''}`}></i>
          </button>
        </div>
      </div>

      {/* 💳 TARJETAS KPI PRINCIPALES */}
      <div className="row g-3 mb-4">

        {/* 1. LIQUIDEZ TOTAL */}
        <div className="col-md-3">
          <div className="card border-0 shadow-sm rounded-3 overflow-hidden text-white" style={{ background: 'linear-gradient(135deg, #00C853 0%, #009624 100%)' }}>
            <div className="card-body p-3">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <span className="small text-uppercase fw-bold opacity-75">Liquidez Total (Bruto)</span>
                  <h3 className="fw-bold my-1">S/ {liquidezTotal.toFixed(2)}</h3>
                  <small className="opacity-75" style={{ fontSize: '0.78rem' }}>
                    <i className="bi bi-arrow-up-circle-fill me-1"></i>Suma de ventas en periodo
                  </small>
                </div>
                <div className="bg-white bg-opacity-25 rounded-circle p-3 d-flex align-items-center justify-content-center">
                  <i className="bi bi-wallet2 fs-2 text-white"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 2. INVERSIÓN EN STOCK */}
        <div className="col-md-3">
          <div className="card border-0 shadow-sm rounded-3 overflow-hidden text-white" style={{ background: 'linear-gradient(135deg, #2A3B5C 0%, #1E293B 100%)' }}>
            <div className="card-body p-3">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <span className="small text-uppercase fw-bold opacity-75">Inversión en Stock</span>
                  <h3 className="fw-bold my-1">S/ {inversionStock.toFixed(2)}</h3>
                  <small className="opacity-75" style={{ fontSize: '0.78rem' }}>
                    <i className="bi bi-box-seam me-1"></i>Compras de stock del periodo: S/ {gastosStockPeriodo.toFixed(2)}
                  </small>
                </div>
                <div className="bg-white bg-opacity-10 rounded-circle p-3 d-flex align-items-center justify-content-center">
                  <i className="bi bi-cart-check fs-2 text-white"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 3. GANANCIA NETA REAL */}
        <div className="col-md-3">
          <div className="card border-0 shadow-sm rounded-3 overflow-hidden text-white" style={{ background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)' }}>
            <div className="card-body p-3">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <span className="small text-uppercase fw-bold opacity-75">Ganancia Neta por Perfiles</span>
                  <h3 className="fw-bold my-1">S/ {gananciaNetaReal.toFixed(2)}</h3>
                  <small className="opacity-75" style={{ fontSize: '0.78rem' }}>
                    <i className="bi bi-graph-up-arrow me-1"></i>Ingresos menos costo asignado por perfil
                  </small>
                </div>
                <div className="bg-white bg-opacity-20 rounded-circle p-3 d-flex align-items-center justify-content-center">
                  <i className="bi bi-cash-stack fs-2 text-white"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 4. TOTAL TRANSACCIONES */}
        <div className="col-md-3">
          <div className="card border-0 shadow-sm rounded-3 overflow-hidden bg-white text-dark border">
            <div className="card-body p-3">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <span className="small text-uppercase fw-bold text-muted">Total de Ventas</span>
                  <h3 className="fw-bold my-1 text-dark">{totalVentasCount}</h3>
                  <small className="text-muted" style={{ fontSize: '0.78rem' }}>
                    <i className="bi bi-receipt me-1"></i>Operaciones registradas
                  </small>
                </div>
                <div className="bg-light rounded-circle p-3 d-flex align-items-center justify-content-center">
                  <i className="bi bi-check-circle-fill fs-2 text-success"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>

      <div className="row g-3 mb-4">
        {[
          { title: 'Reporte diario', period: periodosReporte.diario, summary: reportesPeriodo.diario },
          { title: 'Reporte semanal', period: periodosReporte.semanal, summary: reportesPeriodo.semanal }
        ].map(({ title, period, summary }) => (
          <div className="col-md-6" key={title}>
            <div className="card border-0 shadow-sm rounded-3 bg-white p-3 h-100">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="fw-bold mb-0 text-dark">
                  <i className="bi bi-calendar-check me-2 text-success"></i>{title}
                </h6>
                <span className="small text-muted">{period.label}</span>
              </div>
              <div className="table-responsive">
                <table className="table table-sm align-middle mb-0">
                  <tbody>
                    <tr><td>Ingresos por ventas y renovaciones</td><td className="text-end fw-bold text-success">S/ {summary.ingresoTotal.toFixed(2)}</td></tr>
                    <tr><td>Ventas / renovaciones</td><td className="text-end fw-semibold">{summary.ventas} / {summary.renovaciones}</td></tr>
                    <tr><td>Ticket promedio</td><td className="text-end fw-semibold">S/ {summary.ticketPromedio.toFixed(2)}</td></tr>
                    <tr><td>Gastos de stock</td><td className="text-end fw-semibold">S/ {summary.gastosStock.toFixed(2)}</td></tr>
                    <tr><td>Ganancia estimada por perfiles</td><td className="text-end fw-bold">S/ {summary.gananciaPerfiles.toFixed(2)}</td></tr>
                  </tbody>
                </table>
              </div>
              {summary.ventasConCostoFaltante > 0 && (
                <small className="text-warning mt-2">{summary.ventasConCostoFaltante} operación(es) sin costo de perfil asignado.</small>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* 📊 SECCIÓN DE DESGLOSE POR PLATAFORMA Y RESUMEN */}
      <div className="row g-3 mb-4">
        
        {/* DESGLOSE POR PLATAFORMA (TABLA Y BARRAS) */}
        <div className="col-md-7">
          <div className="card border-0 shadow-sm rounded-3 bg-white p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0 text-dark">
                <i className="bi bi-grid-fill me-2 text-primary"></i>
                Desglose de Liquidez por Plataforma
              </h6>
              <span className="badge bg-light text-dark border">
                {desglosePlataformas.length} Plataformas
              </span>
            </div>

            {desglosePlataformas.length === 0 ? (
              <div className="text-center py-4 text-muted small">
                No hay transacciones registradas para este periodo.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light small text-uppercase">
                    <tr>
                      <th>Plataforma</th>
                      <th className="text-center">Ventas</th>
                      <th>Participación</th>
                      <th className="text-end">Ingresos</th>
                      <th className="text-end">Costo asignado</th>
                      <th className="text-end">Ganancia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {desglosePlataformas.map(item => {
                      const brand = getBrandColor(item.platform);
                      return (
                        <tr key={item.platform}>
                          <td>
                            <span 
                              className="badge rounded-pill px-3 py-1 fw-bold shadow-sm"
                              style={{ backgroundColor: brand.bg, color: brand.text }}
                            >
                              {item.platform}
                            </span>
                          </td>
                          <td className="text-center fw-bold text-dark">
                            {item.sales}
                          </td>
                          <td style={{ width: '35%' }}>
                            <div className="d-flex align-items-center gap-2">
                              <div className="progress flex-grow-1" style={{ height: '8px', backgroundColor: '#e9ecef' }}>
                                <div 
                                  className="progress-bar rounded-pill" 
                                  role="progressbar" 
                                  style={{ width: `${item.porcentaje}%`, backgroundColor: brand.bg }}
                                ></div>
                              </div>
                              <span className="small fw-semibold text-muted" style={{ fontSize: '0.75rem', width: '40px' }}>
                                {item.porcentaje}%
                              </span>
                            </div>
                          </td>
                          <td className="text-end fw-bold text-success">
                            S/ {item.revenue.toFixed(2)}
                          </td>
                          <td className="text-end fw-semibold text-dark">
                            S/ {item.allocatedCost.toFixed(2)}
                            {item.missingCostCount > 0 && <div className="small text-warning">Parcial: faltan {item.missingCostCount}</div>}
                            {item.estimatedCount > 0 && <div className="small text-muted">{item.estimatedCount} estimado(s)</div>}
                          </td>
                          <td className={`text-end fw-bold ${item.profit === null ? 'text-warning' : item.profit >= 0 ? 'text-success' : 'text-danger'}`}>
                            {item.profit === null ? 'No disponible' : `S/ ${item.profit.toFixed(2)}`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* TARJETA INFORMATIVA / AUDIT SUMMARY */}
        <div className="col-md-5">
          <div className="card border-0 shadow-sm rounded-3 bg-white p-3 h-100">
            <h6 className="fw-bold mb-3 text-dark">
              <i className="bi bi-shield-check me-2 text-success"></i>
              Resumen de Operatividad Financiera
            </h6>
            
            <div className="p-3 bg-light rounded-3 mb-3 border">
              <div className="d-flex justify-content-between mb-2">
                <span className="small text-muted">Ingreso Medio por Venta:</span>
                <span className="fw-bold text-dark">
                  S/ {totalVentasCount > 0 ? (liquidezTotal / totalVentasCount).toFixed(2) : '0.00'}
                </span>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="small text-muted">Margen sobre ingresos con costo:</span>
                <span className={`fw-bold ${gananciaNetaReal >= 0 ? 'text-success' : 'text-danger'}`}>
                  {rentabilidad.assignedRevenue > 0 ? ((gananciaNetaReal / rentabilidad.assignedRevenue) * 100).toFixed(1) : '0'}%
                </span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="small text-muted">Base de Datos Conectada:</span>
                <span className="badge bg-success text-white">Supabase PostgreSQL</span>
              </div>
            </div>

            {rentabilidad.estimatedCount > 0 && <div className="alert alert-info border-0 rounded-3 mb-2 small">
              <i className="bi bi-info-circle-fill me-2"></i>
              {rentabilidad.estimatedCount} ventas o renovaciones antiguas usan el costo promedio actual por perfil de su plataforma.
            </div>}
            {rentabilidad.missingCostCount > 0 && <div className="alert alert-warning border-0 rounded-3 mb-2 small">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              Hay {rentabilidad.missingCostCount} ventas o renovaciones (S/ {rentabilidad.missingCostRevenue.toFixed(2)}) sin cuenta comparable. Se excluyen de la ganancia hasta contar con su costo.
            </div>}
            <div className="alert alert-info border-0 rounded-3 mb-0 small">
              <i className="bi bi-info-circle-fill me-2"></i>
              Cada perfil usa el costo de su cuenta dividido entre su capacidad. Los registros históricos vinculados usan esa cuenta; los demás usan el promedio actual de su plataforma.
            </div>
          </div>
        </div>

      </div>

      {/* 📑 HISTORIAL DE VENTAS (AUDITORÍA DE TRANSACCIONES) */}
      <div className="card border-0 shadow-sm rounded-3 bg-white p-3">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div>
            <h6 className="fw-bold mb-0 text-dark">
              <i className="bi bi-journal-text me-2 text-dark"></i>
              Historial Completo de Ventas y Operaciones
            </h6>
            <small className="text-muted">Cliente, contacto, perfil, vigencia y detalle de cada movimiento registrado.</small>
          </div>

          <div className="d-flex align-items-center gap-2">
            <div className="input-group input-group-sm" style={{ width: '250px' }}>
              <span className="input-group-text bg-light border-end-0">
                <i className="bi bi-search text-muted"></i>
              </span>
              <input 
                type="text"
                className="form-control border-start-0 bg-light"
                placeholder="Buscar cliente, plataforma..."
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
              />
              {busqueda && (
                <button className="btn btn-outline-secondary border-start-0" onClick={() => setBusqueda('')}>
                  <i className="bi bi-x"></i>
                </button>
              )}
            </div>
          </div>
        </div>

        {transaccionesFiltradas.length === 0 ? (
          <div className="text-center py-5">
            <i className="bi bi-inbox text-muted opacity-50" style={{ fontSize: '3rem' }}></i>
            <h6 className="fw-bold text-secondary mt-2">No hay transacciones que coincidan</h6>
            <p className="text-muted small mb-0">Revisa los filtros de búsqueda o cambia el mes seleccionado.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-dark">
                <tr>
                  <th>Fecha y Hora</th>
                  <th>Cliente y contacto</th>
                  <th>Servicio y perfil</th>
                  <th>Vigencia</th>
                  <th>Monto / costo perfil</th>
                  <th>Tipo</th>
                  <th>Descripción</th>
                  <th>ID operación</th>
                </tr>
              </thead>
              <tbody>
                {transaccionesFiltradas.map(tx => {
                  const fechaFormateada = tx.date ? dayjs(tx.date).tz(REPORT_TIMEZONE).format('DD/MM/YYYY HH:mm') : 'Sin fecha';
                  const brand = getBrandColor(tx.platform);
                  const isVenta = (tx.type || '').toUpperCase() === 'VENTA';

                  return (
                    <tr key={tx.id}>
                      <td className="small fw-semibold text-muted">
                        <i className="bi bi-clock me-1"></i>{fechaFormateada}
                      </td>
                      <td>
                        <div className="fw-bold text-dark">{tx.client_name || 'Cliente sin vínculo'}</div>
                        {tx.customer_phone && <div className="small text-muted">Teléfono: {tx.customer_phone}</div>}
                        {tx.customer_id && <div className="small text-muted">Cliente #{tx.customer_id}</div>}
                      </td>
                      <td>
                        <span 
                          className="badge rounded-pill px-3 py-1 shadow-sm"
                          style={{ backgroundColor: brand.bg, color: brand.text }}
                        >
                          {normalizarPlataforma(tx.platform) || 'Streaming'}
                        </span>
                        {tx.profile_label && <div className="small text-muted mt-1">Perfil: {tx.profile_label}</div>}
                        {tx.account_id && <div className="small text-muted">Cuenta #{tx.account_id}</div>}
                      </td>
                      <td className="small text-muted">
                        <div>Inicio: {tx.subscription_start_date ? dayjs(tx.subscription_start_date).format('DD/MM/YYYY') : '—'}</div>
                        <div>Vence: {tx.subscription_end_date ? dayjs(tx.subscription_end_date).format('DD/MM/YYYY') : '—'}</div>
                        {tx.subscription_status && <div>Estado: {tx.subscription_status}</div>}
                      </td>
                      <td>
                        <div className="fw-bold text-success">S/ {Number(tx.amount || 0).toFixed(2)}</div>
                        {tx.profile_unit_cost !== null && tx.profile_unit_cost !== undefined && <div className="small text-muted">Costo perfil: S/ {Number(tx.profile_unit_cost).toFixed(2)}</div>}
                      </td>
                      <td>
                        <span className={`badge rounded-pill ${isVenta ? 'bg-success' : 'bg-primary'}`}>
                          {tx.type || 'VENTA'}
                        </span>
                      </td>
                      <td className="small text-muted">
                        {tx.description || 'Operación registrada'}
                      </td>
                      <td className="small text-muted">
                        <div>Operación #{tx.id}</div>
                        {tx.subscription_id && <div>Perfil #{tx.subscription_id}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mt-3 pt-2 border-top">
          <span className="small text-muted">
            Transacciones mostradas: <strong>{transaccionesFiltradas.length}</strong> de <strong>{transacciones.length}</strong>
          </span>
        </div>
      </div>

    </div>
  );
}

export default VistaReportes;
